/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { BrowserModule } from "@angular/platform-browser";
import { NgModule, ErrorHandler } from "@angular/core";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { ScrollToModule } from "@nicky-lenaers/ngx-scroll-to";
import { BsDatepickerModule } from "ngx-bootstrap/datepicker";
import { TimepickerModule } from "ngx-bootstrap/timepicker";
import { ColorPickerModule } from "ngx-color-picker";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { PaginationModule } from "ngx-bootstrap/pagination";
import { TabsModule } from "ngx-bootstrap/tabs";
import { CollapseModule } from "ngx-bootstrap/collapse";
import { ModalModule } from "ngx-bootstrap/modal";
import { OrderFormLibModule } from "./modules/orderformlib/order-form-lib.module";
import { AppRoutingModule } from "./app.routing";

import { AvatarModule } from "ngx-avatar";
import { QuillModule } from "ngx-quill";
import { SlideToggleModule } from "ngx-slide-toggle";
import { AngularDraggableModule } from "angular2-draggable";
import { ChartsModule } from "ng2-charts";

import { AppComponent } from "./app.component";
import { AuthenticationGuard } from "./guards/auth.guard";
import {
  AclActionService,
  CalculationService,
  HttpInterceptorService,
  RecentReportService,
  StageService,
  UserService,
  ReportingService,
  ErrorService,
  ServerErrorInterceptorService,
  ErrorNotificationService,
  TemplateActionService,
  StateService,
  StompClient,
  AccountService,
  UserPersistenceService,
  DashboardService,
  ModeService,
  BoardService,
  RightMenuService,
} from "./services";
import {
  AuthProvider,
  ReportProvider,
  UserProvider,
  ClientProvider,
  ClientUserProvider,
  StageProvider,
  StateProvider,
  ReportHistoryProvider,
  RoleProvider,
  PrivilegeProvider,
  ReportWatchProvider,
  ReportDelayProvider,
  ChecklistProvider,
  ExportProvider,
  ReportingProvider,
  ReportingPresetProvider,
  ReportEntityProvider,
  FileProvider,
  ReportLinksProvider,
  TemplateProvider,
  FieldSetProvider,
  FranchiseProvider,
  PositionProvider,
  ErrorProvider,
  PreferenceProvider,
  StatisticsProvider,
  ReportNotesProvider,
  TagProvider,
  BoardProvider,
  BoardColumnProvider,
  SideNoteProvider,
  QuoteProvider,
  CannedResponseProvider,
  RPiProvider,
  RoTProvider,
  SicProvider,
  ReportIBISProvider,
  DocumentProvider,
  LinkedReportProvider,
  ReportQuoteProvider,
  QuoteLinkProvider,
  RMAProvider,
  HolidayProvider,
} from "./providers";
import {
  AccountPage,
  DashboardPage,
  ForgotPage,
  HomePage,
  LoginPage,
  RecoveryPage,
  TestPage,
  RegisterPage,
  ClientEditPage,
  ClientUserEditPage,
  ChangePasswordPage,
  ProfilePage,
  NotificationPage,
  PreferencesPage,
  ExtensionPage,
} from "./components/pages";
import {
  VersionDisplayComponent,
  FolderSelectComponent,
  ProgressIndicatorComponent,
  LinkExpireComponent,
  ToastComponent,
  ErrorDetailsModal,
  StageProgressHistoryComponent,
  StageHistoryInfoComponent,
  CustomDocumentComponent,
  FeedbackModal,
  MultipleInputSearchComponent,
  FieldContextMenuComponent,
  FieldContextMenuWrapperComponent,
  CustomRatingComponent,
} from "./components/fields";
import { ConfirmDeleteReportModal } from "./components/pages/dashboard/modal/confirm-delete/confirm-delete-report.modal";
import { FilterInputComponent } from "./components/fields/filter-input/filter-input.component";
import { ReportService } from "./services/report.service";
import {
  NotificationGroupPipe,
  ChecklistEntityDetailsBsTogglePipe,
  PositionTypePipe,
  ReportUserTypePipe,
  ModeFIlterPipe,
  CurrencyPipe,
  DateFormatPipe,
} from "./pipes";
import {
  CreateNewClientModal,
  CreateNewClientUserModal,
  ConfirmDeleteUserModal,
  ConfirmDeleteClientModal,
  ChangePasswordModal,
  ConfirmDeleteClientUserModal,
  CreateNewPrivilegeModal,
  AddPrivilegeModal,
  ConfirmDeleteRoleModal,
  ConfirmDeletePrivilegeModal,
  GetLinkModal,
  ManagePresetModal,
  CreatePresetModal,
  CreateNewUserModal,
  CreateNewPositionModal,
  ConfirmDeletePositionModal,
  CreatePreferenceModal,
  UserNoPositionModal,
  UpdatePreferenceModal,
  CreateNewTagModal,
  CreateEditHolidayModal,
  CreateNewBoardModal,
  CreateNewBoardColumnModal,
  DuplicateReportModal,
  TfaModal,
  SecurityNoticeModal,
  GetCompletedReportModal,
  EmailInfoModal,
  ChangeProfilePhotoModal,
  CreateNewFranchiseModal,
} from "./components/modal";
import { ReportPage } from "./components/pages/report/report.page";
import {
  ReportExportPage,
  ReportGeneralPage,
  ReportHistoryPage,
  ReportChecklistPage,
  ReportLinksPage,
  ReportNotesPage,
  ReportProgressComponent,
  PreviewFileComponent,
  ReportHistoryEmailsPage,
} from "./components/pages/report";
import { NewReportModal } from "./components/pages/dashboard/modal/new-report/new-report.modal";

import {
  DueDateComponent,
  StageInfoComponent,
  EntityChargeComponent,
  NotesInfoComponent,
  NotesInfoContentComponent,
  LinkAccessComponent,
} from "./components/pages/report/components";
import { StageInfoContentComponent } from "./components/pages/report/components/stage-info-content/stage-info-content.component";
import {
  ClientInfoContentComponent,
  ClientUserInfoContentComponent,
  UserInfoContentComponent,
} from "./components/pages/report/components/profile-info";
import { DueDateContentComponent } from "./components/pages/report/components/due-date/due-date-content/due-date-content.component";
import {
  ChecklistFinancialsModal,
  ReportDelayModal,
  ReportStageModal,
  EditLinkModal,
  ConfirmDeleteLinkModal,
  EngageReportModal,
  RequestInfoModal,
  ConfirmCancelReportModal,
  InvalidFormModal,
  CopyFinancialYearModal,
  NotificationEngagedModal,
  PreviewPdfModal,
  EngageReportManualModal,
  SiteVisitManualModal,
  ConfirmRevertModal,
  ConfirmAssigneeModal,
  ChangeFinancialDateModal,
  SelectDateModal,
  AssignCollectorModal,
  ConfirmDeleteCustomDocumentModal,
  UnlockReportInfoModal,
  EngageReportFileModal,
  EditReportDelayModal,
  ChangeAssigneeModal,
  ReportMergeModal,
  ReportFollowUpModal,
  ChangeResearchedSpreadedModal,
  DocumentsActionModal,
} from "./components/pages/report/modals";
import { MomentModule } from "ngx-moment";
import { CreateNewRoleModal } from "./components/modal/create-new-role/create-new-role.modal";
import { AddPositionModal } from "./components/modal/add-user-position/add-user-position.modal";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { ClipboardModule } from "ngx-clipboard";
import { GlobalErrorHandler } from "./global-error-handler";
import { AngularEditorModule } from "@kolkov/angular-editor";
import { FormValidationGuard } from "./guards/form.guard";
import { EntityChargeHoverComponent } from "./components/pages/report/components/entity-charge/entity-charge-hover/entity-charge-hover.component";
import { PdfViewerModule } from "ng2-pdf-viewer";
import { FilterPipeModule } from "ngx-filter-pipe";
import { ConfirmNextStageModal } from "./components/pages/dashboard/modal/confirm-stage/confirm-next-stage.modal";
// import {
//   AssigneeInfoContentComponent } from './components/pages/dashboard/components/assignee-info/assignee-content/assignee-content.component';
// import {
//   AssigneeProfileInfoComponent } from './components/pages/dashboard/components/assignee-info/assignee-profile-info.component';
import { ConfirmDeletePreferenceModal } from "./components/pages/dashboard/modal/confirm-delete-preference/confirm-delete-preference.modal";
import { CoreModule } from "./components/fields/core.module";
import { DashboardLoaderComponent } from "./components/fields/dashboard-loader/dashboard-loader.component";
import { StatisticsPage } from "./components/pages/statistics/statistics.page";
import { ReportNoteComponent } from "./components/pages/report/components/report-note/report-note.component";
import { AutosizeModule } from "ngx-autosize";
import { ReportNotesModal } from "./components/pages/dashboard/modal/notes/report-notes.modal";
import { ReportRowComponent } from "./components/pages/dashboard/components/report-row/report-row.component";
import { ReportEaPage } from "./components/pages/report-ea/report-ea.page";
import {
  ReportEaGeneralPage,
  ReportEaHistoryPage,
  ReportEaLinksPage,
  ReportEaNotesPage,
} from "./components/pages/report-ea";
import { SideNotesComponent } from "./components/pages/side-notes/side-notes.component";
import { DraggableDirective, NumbersDirective } from "./directives";
import { AssignReportModal } from "./components/pages/side-notes/assign-report/assign-report.modal";
import { SidenoteReminderComponent } from "./components/modal/sidenote-reminder/sidenote-reminder.component";
import { ReportLinkComponent } from "./components/pages/report/modals/report-link/report-link.component";
import {
  ReportNotesInfoComponent,
  ReportNotesInfoContentComponent,
} from "./components/pages/dashboard/components";
import {
  BVQReportProvider,
  NaicsProvider,
  QuestionBankProvider,
  ZipProvider,
} from "./providers/bvq";
import {
  BVQReportService,
  LockService,
  ReportSourceService,
} from "./modules/bvq/services";
import {
  QuoteComponent,
  QuoteGeneralComponent,
} from "./components/pages/quote";
import { ReportFilesComponent } from "./components/pages/report/report-files/report-files.component";
import { TreeItemComponent } from "./components/pages/report/report-files/tree-item/tree-item.component";
import { CreateFolderModal } from "./components/pages/report/report-files/modals/create-folder/create-folder.modal";
import { UploadFileModal } from "./components/pages/report/report-files/modals/upload-file/upload-file.modal";
import { ContextMenuModule } from "ngx-contextmenu";
import { NgxFileDropModule } from "ngx-file-drop";
import { NgxFilesizeModule } from "ngx-filesize";
import { LookupService } from "./services/lookup.service";
import { TransactionTypeService } from "./services/transaction-type.service";
import { LookupProvider } from "./providers/bvq/lookup.provider";
import { ResearchModule } from "./modules/research/research.module";
import { NgxDropzoneModule } from "ngx-dropzone";
import { ImageCropperModule } from "ngx-image-cropper";
import { ImageCropperModal } from "./components/modal/image-cropper/image-cropper.modal";
import { AssignAnalystModal } from "./components/pages/dashboard/modal/assign-analyst/assign-analyst.modal";
import { NgxSliderModule } from "@angular-slider/ngx-slider";
import { OfcModule } from "./modules/ofc/ofc.module";
import { MiniKanbanComponent } from "./modules/ofc/components/mini-kanban/mini-kanban.component";
import { BankDetailComponent } from "./components/modal/bank-detail/bank-detail.component";
import { SupplementalLinkModal } from "./components/pages/report/modals/supplemental-link/supplemental-link.modal";
import { ExternalAskPage } from "./components/pages/external-ask/external-ask.page";
import { LoggerModule, NgxLoggerLevel } from "ngx-logger";
import { LogService } from "./services/log.service";
import { ReportHistoryModal } from "./components/modal/report-history/report-history.modal";
import { ArchiveReportsModalComponent } from "./components/pages/dashboard/modal/archive-reports/archive-reports-modal.component";
import { BizCompsProvider } from "./providers/bizcomps.provider";
import { DealStateProvider } from "./providers/dealstate.provider";
@NgModule({
  bootstrap: [AppComponent],
  imports: [
    CoreModule,
    // System Modules
    BrowserModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    // Additional Modules
    ScrollToModule.forRoot(),
    BsDropdownModule.forRoot(),
    ModalModule.forRoot(),
    CollapseModule.forRoot(),
    BsDatepickerModule.forRoot(),
    TabsModule.forRoot(),
    PaginationModule.forRoot(),
    AvatarModule,
    AngularEditorModule,
    MomentModule,
    DragDropModule,
    ClipboardModule,
    PdfViewerModule,
    FilterPipeModule,
    AutosizeModule,
    ColorPickerModule,
    QuillModule.forRoot(),
    TimepickerModule.forRoot(),
    SlideToggleModule,
    OrderFormLibModule,
    AngularDraggableModule,
    ChartsModule,
    ContextMenuModule.forRoot({ useBootstrap4: true }),
    NgxFileDropModule,
    NgxFilesizeModule,
    ResearchModule,
    NgxDropzoneModule,
    ImageCropperModule,
    NgxSliderModule,
    OfcModule,
    LoggerModule.forRoot({
      level: NgxLoggerLevel.TRACE,
      serverLogLevel: NgxLoggerLevel.ERROR,
      disableConsoleLogging: false,
    }),
  ],
  declarations: [
    // Core Page
    AppComponent,
    // Public Accessible Pages
    TestPage,
    LoginPage,
    ForgotPage,
    RecoveryPage,
    RegisterPage,
    ExternalAskPage,
    // Protected Pages
    HomePage,
    DashboardPage,
    ClientEditPage,
    ClientUserEditPage,
    ChangePasswordPage,
    ExtensionPage,
    StatisticsPage,
    ReportPage,
    ReportGeneralPage,
    ReportHistoryPage,
    ReportExportPage,
    ReportChecklistPage,
    ReportLinksPage,
    ReportNotesPage,
    ReportHistoryEmailsPage,
    ReportEaPage,
    ReportEaGeneralPage,
    ReportEaHistoryPage,
    ReportEaLinksPage,
    ReportEaNotesPage,
    AccountPage,
    ProfilePage,
    NotificationPage,
    PreferencesPage,
    QuoteComponent,
    QuoteGeneralComponent,
    MiniKanbanComponent,
    // Components & UI Parts
    VersionDisplayComponent,
    FilterInputComponent,
    StageInfoComponent,
    StageInfoContentComponent,
    ClientUserInfoContentComponent,
    ClientInfoContentComponent,
    DueDateComponent,
    DueDateContentComponent,
    FolderSelectComponent,
    ProgressIndicatorComponent,
    LinkExpireComponent,
    ToastComponent,
    StageProgressHistoryComponent,
    StageHistoryInfoComponent,
    EntityChargeComponent,
    EntityChargeHoverComponent,
    CustomDocumentComponent,
    UserInfoContentComponent,
    NotesInfoComponent,
    NotesInfoContentComponent,
    DashboardLoaderComponent,
    ReportNoteComponent,
    ReportRowComponent,
    MultipleInputSearchComponent,
    ReportNotesInfoComponent,
    ReportNotesInfoContentComponent,
    LinkAccessComponent,
    FieldContextMenuComponent,
    FieldContextMenuWrapperComponent,
    ReportProgressComponent,
    CustomRatingComponent,
    // LoaderComponent,
    // Modals
    ConfirmDeleteReportModal,
    CreateNewClientModal,
    CreateNewClientUserModal,
    ConfirmDeleteClientModal,
    ConfirmDeleteUserModal,
    ChangePasswordModal,
    ReportStageModal,
    NewReportModal,
    ConfirmDeleteClientUserModal,
    CreateNewRoleModal,
    CreateNewPrivilegeModal,
    ReportDelayModal,
    AddPrivilegeModal,
    ConfirmDeleteRoleModal,
    ConfirmDeletePrivilegeModal,
    GetLinkModal,
    ManagePresetModal,
    CreatePresetModal,
    ChecklistFinancialsModal,
    EditLinkModal,
    SupplementalLinkModal,
    ConfirmDeleteLinkModal,
    EngageReportModal,
    ErrorDetailsModal,
    RequestInfoModal,
    ConfirmCancelReportModal,
    InvalidFormModal,
    CreateNewUserModal,
    CopyFinancialYearModal,
    NotificationEngagedModal,
    PreviewPdfModal,
    EngageReportManualModal,
    SiteVisitManualModal,
    ConfirmRevertModal,
    ConfirmAssigneeModal,
    ChangeFinancialDateModal,
    SelectDateModal,
    AssignCollectorModal,
    ConfirmDeleteCustomDocumentModal,
    UnlockReportInfoModal,
    CreateNewPositionModal,
    ConfirmDeletePositionModal,
    AddPositionModal,
    ConfirmNextStageModal,
    EngageReportFileModal,
    CreatePreferenceModal,
    FeedbackModal,
    UserNoPositionModal,
    ConfirmDeletePreferenceModal,
    UpdatePreferenceModal,
    EditReportDelayModal,
    ChangeAssigneeModal,
    ReportNotesModal,
    CreateNewTagModal,
    CreateEditHolidayModal,
    ReportMergeModal,
    CreateNewBoardModal,
    CreateNewBoardColumnModal,
    DuplicateReportModal,
    AssignReportModal,
    ReportFollowUpModal,
    ChangeResearchedSpreadedModal,
    TfaModal,
    SecurityNoticeModal,
    DocumentsActionModal,
    GetCompletedReportModal,
    CreateFolderModal,
    UploadFileModal,
    PreviewFileComponent,
    EmailInfoModal,
    ChangeProfilePhotoModal,
    ImageCropperModal,
    AssignAnalystModal,
    CreateNewFranchiseModal,
    BankDetailComponent,
    ReportHistoryModal,
    // Pipes
    DateFormatPipe,
    NotificationGroupPipe,
    ChecklistEntityDetailsBsTogglePipe,
    PositionTypePipe,
    ReportUserTypePipe,
    CreateNewTagModal,
    ModeFIlterPipe,
    SideNotesComponent,
    // Directives
    NumbersDirective,
    SidenoteReminderComponent,
    ReportLinkComponent,
    ReportFilesComponent,
    TreeItemComponent,
    DraggableDirective,
    ArchiveReportsModalComponent,
  ],
  providers: [
    // System Services & Providers
    HttpInterceptorService,
    AuthenticationGuard,
    FormValidationGuard,
    // Providers
    AuthProvider,
    UserProvider,
    ClientProvider,
    ClientUserProvider,
    ReportProvider,
    StageProvider,
    StateProvider,
    ReportHistoryProvider,
    RoleProvider,
    PrivilegeProvider,
    CalculationService,
    ReportWatchProvider,
    ReportDelayProvider,
    ChecklistProvider,
    ExportProvider,
    ReportingProvider,
    ReportingPresetProvider,
    ReportEntityProvider,
    FileProvider,
    ReportLinksProvider,
    FranchiseProvider,
    PositionProvider,
    ErrorProvider,
    PreferenceProvider,
    StatisticsProvider,
    ReportNotesProvider,
    TagProvider,
    BoardProvider,
    BoardColumnProvider,
    SideNoteProvider,
    QuoteProvider,
    CannedResponseProvider,
    DocumentProvider,
    LinkedReportProvider,
    RMAProvider,
    BizCompsProvider,
    DealStateProvider,
    // Template Provider
    TemplateProvider,
    FieldSetProvider,
    TemplateActionService,
    // Services
    RecentReportService,
    UserService,
    ReportService,
    StageService,
    AclActionService,
    ReportingService,
    ErrorService,
    ErrorNotificationService,
    StateService,
    StompClient,
    AccountService,
    UserPersistenceService,
    DashboardService,
    ModeService,
    BoardService,
    RightMenuService,
    TransactionTypeService,
    LookupService,
    LogService,
    // Pipes
    CurrencyPipe,
    DateFormatPipe,
    //BVQ
    BVQReportProvider,
    ZipProvider,
    NaicsProvider,
    RPiProvider,
    RoTProvider,
    SicProvider,
    ReportIBISProvider,
    BVQReportService,
    ReportSourceService,
    LockService,
    LookupProvider,
    QuestionBankProvider,
    //Quote
    ReportQuoteProvider,
    QuoteLinkProvider,
    HolidayProvider,
    // Bindings
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorService,
      multi: true,
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerErrorInterceptorService,
      multi: true,
    },
  ],
})
export class AppModule {}
